import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';

export interface ClearFiltersButtonProps extends Omit<ButtonProps, 'onClick'> {
  /**
   * Mandatory callback function triggered when the user clicks the button to reset data.
   */
  onClear: () => void;
  /**
   * Optional text text display inside the button.
   * @default "Azzera filtri"
   */
  label?: string;
}

/**
 * A reusable, styled action button designed for clearing active table filters or form states.
 * Inherits all standard Material-UI Button properties except `onClick` (replaced by `onClear`).
 *
 * @component
 * @example
 * ```tsx
 * <ClearFiltersButton onClear={handleClearAll} label="Reset All" />
 * ```
 */
export const ClearFiltersButton = ({
  onClear,
  label = 'Azzera filtri',
  size = 'medium',
  sx,
  ...rest
}: ClearFiltersButtonProps) => {
  return (
    <Button
      {...rest}
      size={size}
      startIcon={<DeleteSweepIcon />}
      onClick={onClear}
      sx={{
        color: 'error.main',
        textTransform: 'none', // Evita il tutto maiuscolo automatico di MUI
        fontWeight: 500,
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: 'none',
          color: 'error.dark',
          backgroundColor: 'rgba(211, 47, 47, 0.06)'
        },
        '&:active': {
          backgroundColor: 'rgba(211, 47, 47, 0.12)'
        },
        ...sx
      }}>
      {label}
    </Button>
  );
};
